/**
 * Iter 68d — Admin Chat Leads tile.
 *
 * Surfaces every name/email/phone captured from the proactive chat
 * bubble's lead-capture form. Mounted on Admin → Operations.
 *
 * Behaviour:
 *   • Lists the most recent N leads (default 60-day window, 50 rows).
 *   • Free-text search across name + email + phone.
 *   • CSV export button — pulls up to 5000 matching rows.
 *   • Surfaces `message_count` + `handoff_count` so the operator can
 *     spot the highest-intent visitors at a glance.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare, Mail, Phone, Search, Download, ExternalLink,
  Loader2, RefreshCw, Sparkles, ShieldCheck, Volume2, VolumeX,
} from "lucide-react";

const REFRESH_INTERVAL_MS = 30_000; // gentle auto-refresh
const MUTE_KEY = "rentalcar:admin_chat_leads_mute";

/**
 * Two-note chime (A5 → E6, ~0.6s) via Web Audio API — same pattern
 * as the vendor handoffs panel. No audio file dependency, works
 * offline, and respects the mute toggle.
 */
function _playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };
    tone(880, 0, 0.28);    // A5
    tone(1318.5, 0.22, 0.34); // E6
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
  } catch {
    /* swallow — chime must never break the dashboard */
  }
}

export default function AdminChatLeadsTile() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [days, setDays] = useState(60);
  // Iter 68e — mute toggle persisted across sessions.
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
  });
  // Highlight badge for the newest captured lead so the admin's eye
  // jumps to it even if they were scrolled away. Cleared after 30s.
  const [newLeadIds, setNewLeadIds] = useState(() => new Set());
  // Keep the previous total + the first-seen id set across polls so
  // we can detect a strictly-increasing count and play the chime.
  const lastTotalRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem(MUTE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("days", String(opts.days ?? days));
      params.set("limit", "50");
      const queryText = opts.q ?? q;
      if (queryText?.trim()) params.set("q", queryText.trim());
      const { data } = await api.get(`/admin/chat-leads?${params}`);
      const fresh = Array.isArray(data?.rows) ? data.rows : [];
      const newTotal = Number(data?.total) || 0;

      // Detect freshly-captured leads — anything whose id wasn't in the
      // last poll. On the very first load we just seed the seen-set
      // without ringing the chime.
      const previouslySeen = seenIdsRef.current;
      const isFirstLoad = previouslySeen.size === 0 && lastTotalRef.current === null;
      const justArrived = new Set();
      for (const row of fresh) {
        if (!previouslySeen.has(row.id)) {
          justArrived.add(row.id);
        }
      }

      if (!isFirstLoad && justArrived.size > 0 && newTotal > (lastTotalRef.current || 0)) {
        if (!muted) _playChime();
        const previewName = fresh.find((r) => justArrived.has(r.id))?.name || "Anonymous";
        toast.success(
          justArrived.size === 1
            ? `New chat lead · ${previewName}`
            : `${justArrived.size} new chat leads captured`,
          { duration: 6000 }
        );
        setNewLeadIds((prev) => {
          const next = new Set(prev);
          for (const id of justArrived) next.add(id);
          return next;
        });
        // Auto-fade the highlight after 30s.
        const stale = new Set(justArrived);
        setTimeout(() => {
          setNewLeadIds((prev) => {
            const next = new Set(prev);
            for (const id of stale) next.delete(id);
            return next;
          });
        }, 30_000);
      }

      // Update tracked sets for the next poll.
      const nextSeen = new Set(previouslySeen);
      for (const row of fresh) nextSeen.add(row.id);
      seenIdsRef.current = nextSeen;
      lastTotalRef.current = newTotal;

      setRows(fresh);
      setTotal(newTotal);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, days, muted]);

  useEffect(() => { load(); }, [load]);

  // Gentle auto-refresh so a freshly-captured lead shows up without
  // the admin clicking refresh manually.
  useEffect(() => {
    const id = setInterval(() => { load(); }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    load();
  };

  const onExport = async () => {
    try {
      const token = localStorage.getItem("rentalcar:token") || localStorage.getItem("token") || "";
      const url = new URL(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/chat-leads.csv`
      );
      url.searchParams.set("days", "365");
      if (q.trim()) url.searchParams.set("q", q.trim());
      const resp = await fetch(url.toString(), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `chat-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(`Export failed: ${e?.message || e}`);
    }
  };

  const formattedRows = useMemo(() => rows.map((r) => ({
    ...r,
    captured_at_display: r.captured_at
      ? new Date(r.captured_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
      : "—",
    last_message_display: r.last_message_at
      ? new Date(r.last_message_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })
      : null,
  })), [rows]);

  return (
    <section className="rounded-3xl border border-neutral-200 bg-white p-6" data-testid="admin-chat-leads">
      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center ring-1 ring-amber-200">
              <Sparkles className="h-5 w-5" />
            </span>
            Chat Leads
          </h2>
          <p className="text-sm text-neutral-500 mt-1.5 max-w-2xl leading-relaxed">
            Every anonymous visitor who started a chat must enter their name, email, and phone/WhatsApp.
            They land here so you can follow up. Logged-in customers don't appear because we already have their details on the user record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={toggleMute}
            variant="outline"
            size="sm"
            className={`rounded-full ${muted ? "border-neutral-300 text-neutral-500" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
            data-testid="admin-chat-leads-mute"
            title={muted ? "Sound notifications muted — click to enable" : "Sound notifications active — click to mute"}
          >
            {muted ? <VolumeX className="h-3.5 w-3.5 mr-1" /> : <Volume2 className="h-3.5 w-3.5 mr-1" />}
            {muted ? "Muted" : "Chime on"}
          </Button>
          <Button
            type="button"
            onClick={() => load()}
            variant="outline"
            size="sm"
            className="rounded-full"
            disabled={loading}
            data-testid="admin-chat-leads-refresh"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
          <Button
            type="button"
            onClick={onExport}
            variant="outline"
            size="sm"
            className="rounded-full border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="admin-chat-leads-export"
          >
            <Download className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
        </div>
      </header>

      <form onSubmit={onSubmitSearch} className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400 pointer-events-none" />
          <Input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="pl-9 rounded-full h-9 text-sm"
            data-testid="admin-chat-leads-search"
          />
        </div>
        <select
          value={days}
          onChange={(e) => { setDays(Number(e.target.value)); load({ days: Number(e.target.value) }); }}
          className="h-9 px-3 rounded-full bg-white border border-neutral-200 text-xs font-medium text-neutral-700"
          data-testid="admin-chat-leads-days"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last year</option>
        </select>
        <Button type="submit" size="sm" className="rounded-full">Search</Button>
      </form>

      <div className="text-xs text-neutral-500 mb-3 flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3 text-emerald-600" />
        {total} lead{total === 1 ? "" : "s"} captured · showing {formattedRows.length}
      </div>

      {formattedRows.length === 0 ? (
        <div className="text-center py-12 text-neutral-500" data-testid="admin-chat-leads-empty">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
          <div className="text-sm">
            {loading
              ? "Loading leads…"
              : q.trim()
                ? `No leads match "${q}".`
                : "No chat leads captured yet — proactive popup is active on every public page."}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-neutral-200" data-testid="admin-chat-leads-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">
              <tr>
                <th className="px-3 py-2.5 text-left">Captured</th>
                <th className="px-3 py-2.5 text-left">Name</th>
                <th className="px-3 py-2.5 text-left">Email</th>
                <th className="px-3 py-2.5 text-left">Phone / WhatsApp</th>
                <th className="px-3 py-2.5 text-center">Msgs</th>
                <th className="px-3 py-2.5 text-center">Handoff</th>
                <th className="px-3 py-2.5 text-left">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {formattedRows.map((r) => {
                const isNew = newLeadIds.has(r.id);
                return (
                <tr
                  key={r.id}
                  className={`transition ${isNew ? "bg-amber-50 ring-1 ring-amber-200 ring-inset animate-pulse" : "hover:bg-amber-50/30"}`}
                  data-testid={`admin-chat-lead-row-${r.id}`}
                >
                  <td className="px-3 py-2.5 text-[12px] text-neutral-600 whitespace-nowrap">
                    {isNew && (
                      <span className="mr-1.5 inline-block text-[9px] uppercase tracking-wider font-bold text-amber-800 bg-amber-200 ring-1 ring-amber-300 px-1.5 py-0.5 rounded-full">
                        NEW
                      </span>
                    )}
                    {r.captured_at_display}
                    {r.last_message_display && (
                      <div className="text-[10px] text-neutral-400">last msg {r.last_message_display}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-neutral-900">
                    {r.name}
                    {r.marketing_consent && (
                      <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 ring-1 ring-emerald-100 px-1.5 py-0.5 rounded-full">opt-in</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[12px]">
                    <a href={`mailto:${r.email}`} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {r.email}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-[12px]">
                    <a
                      href={`https://wa.me/${r.phone_digits || (r.phone || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Phone className="h-3 w-3" /> {r.phone}
                    </a>
                  </td>
                  <td className="px-3 py-2.5 text-center text-[12px] font-mono">
                    {r.message_count > 0 ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{r.message_count}</span>
                    ) : (
                      <span className="text-neutral-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center text-[12px] font-mono">
                    {r.handoff_count > 0 ? (
                      <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{r.handoff_count}</span>
                    ) : (
                      <span className="text-neutral-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-neutral-500 max-w-[200px] truncate">
                    {r.source_path ? (
                      <a href={r.source_path} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 inline-flex items-center gap-1">
                        {r.source_path} <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
